import React, { useEffect, useState } from 'react';
import { X, Share2, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

// Modal for sharing a document with a single partner org. Only orgs with
// an `accepted` partnership are listed. Optional message + optional expiry.
export default function ShareDocumentModal({ document, onClose, onShared }) {
  const { currentOrg } = useOrg();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState('');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentOrg) return;
    api.getActivePartners(currentOrg.id)
      .then(setPartners)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentOrg]);

  const handleSubmit = async () => {
    if (!recipientId) { setError('Choose a partner organization'); return; }
    setSubmitting(true); setError(null);
    try {
      const expires_at = hasExpiry
        ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
        : null;
      await api.shareDocument(currentOrg.id, document.id, {
        recipient_org_id: recipientId,
        message: message.trim() || null,
        expires_at,
      });
      onShared();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-[480px] bg-elevated border border-theme rounded-[16px] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-theme flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-[36px] h-[36px] rounded-[10px] bg-accent-glow flex items-center justify-center text-accent-light">
              <Share2 size={16} />
            </div>
            <div>
              <h3 className="text-[14px] font-bold text-primary-c">Share document</h3>
              <p className="text-[11px] text-muted-c truncate max-w-[300px]">{document.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-c hover:text-primary-c"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-[10px] bg-danger/10 border border-danger/30 text-[12px] text-danger">
              {error}
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-secondary-c mb-2 uppercase tracking-wide">Send to</label>
            {loading ? (
              <div className="text-[12px] text-muted-c flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Loading your partners…
              </div>
            ) : partners.length === 0 ? (
              <div className="p-3 rounded-[10px] bg-tertiary text-[12px] text-muted-c">
                You don't have any active partnerships yet. Connect with another org from the Directory first.
              </div>
            ) : (
              <select
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                className="w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-[13px] text-primary-c focus:outline-none focus:border-[#0DA7CA]"
              >
                <option value="">Choose a partner organization…</option>
                {partners.map(p => (
                  <option key={p.id} value={p.partner_org?.id}>
                    {p.partner_org?.name} ({p.partner_org?.type === 'medical_practice' ? 'Medical' : 'Law Firm'})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-secondary-c mb-2 uppercase tracking-wide">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Anything they should know about this document?"
              className="w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-[13px] text-primary-c focus:outline-none focus:border-[#0DA7CA] resize-none"
            />
          </div>

          <div className="flex items-start gap-3 p-3 rounded-[10px] bg-tertiary border border-theme">
            <input
              type="checkbox"
              id="expiry-toggle"
              checked={hasExpiry}
              onChange={(e) => setHasExpiry(e.target.checked)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <label htmlFor="expiry-toggle" className="text-[12px] font-semibold text-primary-c cursor-pointer">
                Expire access after a set period
              </label>
              {hasExpiry && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    min={1} max={365}
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(Number(e.target.value))}
                    className="w-[80px] px-2.5 py-1.5 bg-elevated border border-strong rounded-[8px] text-[13px] text-primary-c"
                  />
                  <span className="text-[12px] text-muted-c">days from now</span>
                </div>
              )}
              {!hasExpiry && (
                <p className="text-[11px] text-muted-c mt-1">
                  No expiry — the recipient keeps access until you revoke it.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-theme flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-[10px] text-[12px] font-semibold text-muted-c hover:text-primary-c hover:bg-tertiary transition-all"
          >Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !recipientId || partners.length === 0}
            className="px-5 py-2.5 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50"
          >
            {submitting ? 'Sharing…' : 'Share document'}
          </button>
        </div>
      </div>
    </div>
  );
}
